import { lazy, Suspense, useState, useCallback, type ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SEO_LOCALE_URL_STRATEGY } from '@/lib/seo';
import { toPublicPath } from '@/lib/localizedRoutes.shared.js';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingContact from "@/components/FloatingContact";
import CursorFollower from "@/components/CursorFollower";
import Analytics from "@/components/Analytics";
import LeadAttributionBootstrap from "@/components/LeadAttributionBootstrap";
import { RouteTransitionLayout, TransitionFallback } from "@/components/PageTransition";
import Preloader from "@/components/Preloader";
import Index from "./pages/Index.tsx";

const ServicesPage = lazy(() => import("./pages/ServicesPage.tsx"));
import CookieConsentBanner from "@/components/cookies/CookieConsentBanner";
import { COOKIE_POLICY_PATH } from "@/lib/cookieConsent";

const AIAnswersPage = lazy(() => import("./pages/AIAnswersPage.tsx"));
const AiAnswerDetailPage = lazy(() => import("./pages/AiAnswerDetailPage.tsx"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage.tsx"));
const SolutionsPage = lazy(() => import("./pages/SolutionsPage.tsx"));
const SolutionDetailPage = lazy(() => import("./pages/SolutionDetailPage.tsx"));
const PlaybooksPage = lazy(() => import("./pages/PlaybooksPage.tsx"));
const PlaybookDetailPage = lazy(() => import("./pages/PlaybookDetailPage.tsx"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage.tsx"));
const AboutPage = lazy(() => import("./pages/AboutPage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage.tsx"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage.tsx"));
const ToolsPage = lazy(() => import("./pages/ToolsPage.tsx"));
const ToolDetailPage = lazy(() => import("./pages/ToolDetailPage.tsx"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage.tsx"));
const TermsPage = lazy(() => import("./pages/TermsPage.tsx"));
const KvkkPage = lazy(() => import("./pages/KvkkPage.tsx"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage.tsx"));
const DeliveryRefundPage = lazy(() => import("./pages/DeliveryRefundPage.tsx"));
const DistanceSalesPage = lazy(() => import("./pages/DistanceSalesPage.tsx"));
const GuideDetailPage = lazy(() => import("./pages/GuideDetailPage.tsx"));
const GuidesPage = lazy(() => import("./pages/GuidesPage.tsx"));
const BlogTagPage = lazy(() => import("./pages/BlogTagPage.tsx"));
const AuthorDetailPage = lazy(() => import("./pages/AuthorDetailPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AiProductPage = lazy(() => import("./pages/ads/AiProductPage.tsx"));
const MobileAppPage = lazy(() => import("./pages/ads/MobileAppPage.tsx"));
const DigitalTransformPage = lazy(() => import("./pages/ads/DigitalTransformPage.tsx"));
const MvpStartupPage = lazy(() => import("./pages/ads/MvpStartupPage.tsx"));
const LinkedInCompanyPage = lazy(() => import("./pages/LinkedInCompanyPage.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function localeRoute(path: string, element: ReactNode) {
  const routes = [<Route key={`en:${path}`} path={path} element={element} />];
  if (SEO_LOCALE_URL_STRATEGY === 'path') {
    const trPath = toPublicPath(path, 'tr');
    routes.push(<Route key={`tr:${trPath}`} path={trPath} element={element} />);
  }
  return routes;
}

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <RouteTransitionLayout pathname={location.pathname}>
      <Suspense fallback={<TransitionFallback />}>
        <Routes location={location} key={location.pathname}>
          {localeRoute('/', <Index />)}
          {localeRoute('/services', <ServicesPage />)}
          {localeRoute('/services/:slug', <ServiceDetailPage />)}
          {localeRoute('/solutions', <SolutionsPage />)}
          {localeRoute('/solutions/:slug', <SolutionDetailPage />)}
          {localeRoute('/playbooks', <PlaybooksPage />)}
          {localeRoute('/playbooks/:slug', <PlaybookDetailPage />)}
          {localeRoute('/projects', <ProjectsPage />)}
          {localeRoute('/projects/:id', <ProjectDetailPage />)}
          {localeRoute('/about', <AboutPage />)}
          {localeRoute('/linkedin', <LinkedInCompanyPage />)}
          {localeRoute('/ai-answers', <AIAnswersPage />)}
          {localeRoute('/ai-answers/:slug', <AiAnswerDetailPage />)}
          {localeRoute('/contact', <ContactPage />)}
          {localeRoute('/tesekkurler', <ThankYouPage />)}
          {localeRoute('/blog', <BlogPage />)}
          {localeRoute('/blog/tag/:tagSlug', <BlogTagPage />)}
          {localeRoute('/blog/:slug', <BlogDetailPage />)}
          {localeRoute('/authors/:slug', <AuthorDetailPage />)}
          {localeRoute('/tools', <ToolsPage />)}
          {localeRoute('/tools/:id', <ToolDetailPage />)}
          {localeRoute('/privacy', <PrivacyPage />)}
          {localeRoute('/kvkk-aydinlatma-metni', <KvkkPage />)}
          {localeRoute('/terms', <TermsPage />)}
          {localeRoute(COOKIE_POLICY_PATH, <CookiePolicyPage />)}
          {localeRoute('/delivery-cancellation-refund', <DeliveryRefundPage />)}
          {localeRoute('/distance-sales-agreement', <DistanceSalesPage />)}
          {localeRoute('/guides', <GuidesPage />)}
          {localeRoute('/guides/:slug', <GuideDetailPage />)}
          <Route path="/ads/ai" element={<AiProductPage />} />
          <Route path="/ads/mobile" element={<MobileAppPage />} />
          <Route path="/ads/digital" element={<DigitalTransformPage />} />
          <Route path="/ads/mvp" element={<MvpStartupPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </RouteTransitionLayout>
  );
};

const AppLayout = () => {
  const location = useLocation();
  const isAdPage = location.pathname.startsWith('/ads');

  return (
    <div data-i18n-surface>
      {!isAdPage && <Navbar />}
      <AnimatedRoutes />
      <FloatingContact />
      <CookieConsentBanner />
      {!isAdPage && <Footer />}
    </div>
  );
};

const App = () => {
  const [loaded, setLoaded] = useState(false);
  const handleComplete = useCallback(() => {
    setLoaded(true);
    (window as Window & { __preloaderDone?: boolean }).__preloaderDone = true;
    window.dispatchEvent(new Event('preloader-done'));
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <LanguageProvider>
                {!loaded && <Preloader onComplete={handleComplete} />}
                <Analytics />
                <LeadAttributionBootstrap />
                <CursorFollower />
                <AppLayout />
              </LanguageProvider>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
